import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@saleor/components/Checkbox";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import useSearchQuery from "@saleor/hooks/useSearchQuery";
import { buttonMessages } from "@saleor/intl";
import { SearchBrands_search_edges_node } from "@saleor/searches/types/SearchBrands";
import useScrollableDialogStyle from "@saleor/styles/useScrollableDialogStyle";
import { FetchMoreProps } from "@saleor/types";
import React from "react";
import InfiniteScroll from "react-infinite-scroller";
import { FormattedMessage, useIntl } from "react-intl";
export interface FormData {
  brands: SearchBrands_search_edges_node[];
  query: string;
}

const useStyles = makeStyles(
  {
    avatar: {
      "&&:first-child": {
        paddingLeft: 0
      },
      width: 72
    },
    checkboxCell: {
      paddingLeft: 0,
      width: 88
    },
    colName: {
      paddingLeft: 0
    }
  },
  { name: "AssignBrandDialog" }
);

export interface AssignBrandDialogProps extends FetchMoreProps {
  brands: any;
  confirmButtonState: ConfirmButtonTransitionState;
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onFetch: (value: string) => void;
  onSubmit: (data: SearchBrands_search_edges_node[]) => void;
}

function handleBrandsAssign(
  brand: SearchBrands_search_edges_node,
  isSelected: boolean,
  selectedBrands: SearchBrands_search_edges_node[],
  setSelectedBrands: (data: SearchBrands_search_edges_node[]) => void
) {
  if (isSelected) {
    setSelectedBrands(
      selectedBrands.filter(selectedBrands => selectedBrands.id !== brand.id)
    );
  } else {
    // setSelectedProducts([...selectedProducts, product]);
    setSelectedBrands([brand]);
  }
}

const AssignBrandDialog: React.FC<AssignBrandDialogProps> = props => {
  const {
    brands,
    confirmButtonState,
    hasMore,
    open,
    loading,
    onClose,
    onFetch,
    onFetchMore,
    onSubmit
  } = props;
  const classes = useStyles(props);
  const scrollableDialogClasses = useScrollableDialogStyle({});

  const intl = useIntl();
  const [query, onQueryChange] = useSearchQuery(onFetch);
  const [selectedBrands, setSelectedBrands] = React.useState<
    SearchBrands_search_edges_node[]
  >([]);
  const container = React.useRef<HTMLDivElement>();

  const handleSubmit = () => onSubmit(selectedBrands);

  const containerHeight = container.current?.scrollHeight - 130;
  const initialForm = {
    id: null,
    name: "",
    sellerBrand: ""
  };

  return (
    <Form initial={initialForm} onSubmit={handleSubmit} confirmLeave>
      {({ submit }) => {
        const btnDisabled = false;

        return (
          <Dialog
            onClose={onClose}
            open={open}
            classes={{ paper: scrollableDialogClasses.dialog }}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              <FormattedMessage
                defaultMessage="Assign Brand"
                description="dialog header"
              />
            </DialogTitle>
            <DialogContent
              className={scrollableDialogClasses.content}
              ref={container}
            >
              <TextField
                name="query"
                value={query}
                onChange={onQueryChange}
                label={intl.formatMessage({
                  defaultMessage: "Search Brands"
                })}
                placeholder={intl.formatMessage({
                  defaultMessage: "Search by name..."
                })}
                fullWidth
                InputProps={{
                  autoComplete: "off",
                  endAdornment: loading && <CircularProgress size={16} />
                }}
              />
              <FormSpacer />
              <div
                className={scrollableDialogClasses.scrollArea}
                style={{ height: containerHeight }}
              >
                <InfiniteScroll
                  pageStart={0}
                  loadMore={onFetchMore}
                  hasMore={hasMore}
                  useWindow={false}
                  loader={
                    <div
                      className={
                        scrollableDialogClasses.loadMoreLoaderContainer
                      }
                      key={0}
                    >
                      <CircularProgress size={16} />
                    </div>
                  }
                  threshold={10}
                >
                  <ResponsiveTable key="table">
                    <TableBody>
                      {brands &&
                        brands.map(brand => {
                          const isSelected = selectedBrands.some(
                            selectedBrands => selectedBrands.id === brand.id
                          );

                          return (
                            <TableRow key={brand.id}>
                              <TableCell className={classes.colName}>
                                {brand.name}
                              </TableCell>
                              <TableCell
                                padding="checkbox"
                                className={classes.checkboxCell}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() =>
                                    handleBrandsAssign(
                                      brand,
                                      isSelected,
                                      selectedBrands,
                                      setSelectedBrands
                                    )
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </ResponsiveTable>
                </InfiniteScroll>
                {brands && brands.length ? "" : "No Brands Found"}
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>
                <FormattedMessage {...buttonMessages.back} />
              </Button>
              <ConfirmButton
                transitionState={confirmButtonState}
                color="primary"
                variant="contained"
                type="submit"
                onClick={submit}
                disabled={btnDisabled || selectedBrands.length === 0}
              >
                <FormattedMessage
                  defaultMessage="Assign brand"
                  description="button"
                />
              </ConfirmButton>
            </DialogActions>
          </Dialog>
        );
      }}
    </Form>
  );
};
AssignBrandDialog.displayName = "AssignBrandDialog";
export default AssignBrandDialog;
